import { useEffect, useMemo } from 'react'
import { RouterProvider } from '@tanstack/react-router'
import { canSee, useSetLocale } from '@lb/domain'
import { Skeleton } from '@lb/ui'

import { hasStoredUiLocale, pathInLocale, useTranslation } from '@lb/i18n'

import { http } from '../lib/client'
import { ConsentBanner, ConsentGate } from './shell/consent-banner'
import { useSessionContext } from './providers/session'
import { makeRouter } from './router/router'

/**
 * The router, fed the session as context so guards can read it synchronously.
 *
 * Built once and re-fed rather than rebuilt: a new router instance per render
 * would remount the whole tree on every session refetch.
 *
 * ── Why the router does not mount until the session has resolved ───────────
 * `beforeLoad` runs once, at navigation. On a cold load — someone pasting
 * `/cases` into the address bar, which is exactly the case a guard exists for —
 * the session was still in flight, `context.role` was `undefined`, and the
 * guard passed. The nav hid the link and the URL rendered anyway. Caught by
 * opening the page rather than by any test.
 *
 * Waiting is the fix rather than re-running guards on context change: the shell
 * cannot draw its navigation without a role either, so there is nothing useful
 * to paint in that window. We show the page shell, so the wait reads as loading
 * rather than as nothing.
 *
 * The server remains the enforcement point — a guard exists so nobody is shown
 * a door they cannot open, not to keep them out.
 */
export function App() {
  const { viewer, isError } = useSessionContext()
  const router = useMemo(() => makeRouter({ role: undefined, canSee }), [])

  useReconcileLocale(viewer?.session.locale)

  const sessionResolved = viewer !== null || isError
  if (!sessionResolved) return <BootSkeleton />

  return (
    <>
      <SkipLink />
      <RouterProvider router={router} context={{ role: viewer?.session.platformRole, canSee }} />
      {/* Outside the router: consent is asked of a visitor, not only a member. */}
      <ConsentGate />
      <ConsentBanner />
    </>
  )
}

/**
 * Keep the account's language and this browser's in step, in whichever
 * direction has something to say.
 *
 * ── Two rules, and which one fires depends on who has stated a preference ──
 * **Nothing stored here, account says something** → follow the account. A
 * member who chose French once should be in French on their phone, their laptop
 * and a borrowed computer.
 *
 * **Stored here, account says something else** → push it up. This is the safety
 * net under the toggle's own write: that request races a full-page navigation
 * and survives it only because it is `keepalive`, which is the right mechanism
 * and not one we can observe failing. If it ever does, the next page load
 * corrects it silently rather than leaving a preference the member believes
 * they set.
 *
 * Neither rule ever acts on a GUESS. `hasStoredUiLocale` is what separates a
 * pressed button from `navigator.language`, and writing the latter would mean
 * signing in on a hotel computer rewrote which language your security mail
 * arrives in.
 *
 * ── Why the download direction navigates ──────────────────────────────────
 * Each language is its own URL and the router's basepath is fixed at
 * construction, so following the account means moving. Three guards on it:
 *
 * **The path must not already name a language.** `/en/…` is a real URL a
 * crawler fetches and a static host answers with English HTML. Redirecting it
 * because the account says `fr` would mean a link shared in English opened in
 * French — the recipient would not see the page the sender sent.
 *
 * **`?lang` must be absent**, so this does not fight a hand-written link that
 * is mid-flight.
 *
 * **Only `en` needs it.** French is served unprefixed; there is nowhere to go.
 */
function useReconcileLocale(accountLocale: string | null | undefined) {
  const { viewer } = useSessionContext()
  const setLocale = useSetLocale(http)
  const { i18n } = useTranslation()

  /* `mutate` is stable; naming it keeps the effect from re-running on every
     render of a mutation object. */
  const { mutate: pushLocale } = setLocale

  useEffect(() => {
    if (!viewer) return

    const chosenHere = hasStoredUiLocale()
    const interfaceLocale = i18n.language === 'en' ? 'en' : 'fr'

    /*
     * ── Only when they DISAGREE, and that is not a micro-optimisation ──────
     * Every accepted write to this route emits a permanent `audit_events` row,
     * including one that sets the value the account already held — the backend
     * does not compare first, on the grounds that *somebody asked for this* is
     * a fact worth keeping.
     *
     * So an unconditional push on every load would write one permanent audit
     * row per navigation, and the trail would read as a member changing their
     * language dozens of times a day. Do not simplify this into `pushLocale`
     * without the guard.
     */
    if (chosenHere) {
      if (accountLocale !== interfaceLocale) pushLocale(interfaceLocale)
      return
    }

    if (accountLocale !== 'en') return

    const { pathname, search } = window.location
    if (pathname === '/en' || pathname.startsWith('/en/')) return
    if (new URLSearchParams(search).has('lang')) return

    /*
     * `pathInLocale`, never a bare prefix: the pathname may already carry the
     * `/fr` alias, and `/en${'/fr/resources'}` is `/en/fr/resources` — a URL
     * nothing serves. That is exactly how an FR deep link opened by an
     * EN-account member landed on «Not Found» in the QA round.
     */
    window.location.assign(`${pathInLocale(pathname, 'en')}${search}`)
  }, [accountLocale, viewer, i18n.language, pushLocale])
}

/**
 * The first tab stop on every page, past the whole navigation.
 *
 * Here rather than in `index.html` because it is copy, and copy in static
 * markup cannot follow the member's language — it was pinned to French for
 * every English reader, which is exactly the person most likely to be using a
 * keyboard-driven reading order in the first place.
 *
 * Outside the router so it exists on the public funnel too, not only behind the
 * app shell.
 */
function SkipLink() {
  const { t } = useTranslation()
  return (
    <a
      href="#main"
      className="sr-only rounded-[var(--r-sm)] bg-ink px-4 py-2 text-[14px] font-semibold text-cream focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50"
    >
      {t('nav.skip')}
    </a>
  )
}

/**
 * The shell's geometry, not a spinner.
 *
 * "Skeletons over spinners" applies to the boot too — this is the sidebar and
 * the content column in outline, so the layout does not jump when it fills.
 */
function BootSkeleton() {
  return (
    <div className="min-h-dvh bg-canvas md:grid md:grid-cols-[var(--sidebar-w)_1fr]">
      <div className="hidden border-r border-border bg-surface p-5 md:block">
        <Skeleton className="mb-8 h-8 w-32" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      </div>
      <div className="p-8">
        <Skeleton className="mb-4 h-10 w-64" />
        <Skeleton className="h-5 w-96" />
      </div>
    </div>
  )
}
